import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { InputOutputType, ModuleSpecification, UIProperty } from '@ca/core/models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { tap } from 'rxjs/operators';
import { ModuleSpecService } from '../module-spec.service';

@UntilDestroy()
@Component({
    selector: 'ca-module-spec-visualiser',
    templateUrl: './module-spec-visualiser.component.html',
    styleUrls: ['./module-spec-visualiser.component.scss'],
})
export class ModuleSpecVisualiserComponent implements OnInit {
    inputs: Array<UIProperty>;
    outputs: Array<UIProperty>;

    @Input() specification: ModuleSpecification;
    @Input() moduleTitle: string;
    @Input() isReadonly: boolean = false;
    @Input() isFormValid: boolean = true;
    @Input() propertyDefinitionComplete: boolean = false;

    @Output() onAddInputOutput: EventEmitter<InputOutputType> = new EventEmitter<InputOutputType>();
    @Output() onRemoveInputOutput: EventEmitter<{ parameter: UIProperty; type: InputOutputType }> = new EventEmitter<{
        parameter: UIProperty;
        type: InputOutputType;
    }>();
    @Output() onSetActiveProp: EventEmitter<UIProperty | undefined> = new EventEmitter<UIProperty | undefined>();

    constructor(private moduleSpecService: ModuleSpecService) {}

    ngOnInit(): void {
        this.inputs = this.moduleSpecService.getUiProperties(InputOutputType.Input);
        this.outputs = this.moduleSpecService.getUiProperties(InputOutputType.Output);

        this.moduleSpecService.activePropertyChanged$
            .pipe(
                tap(() => {
                    this.inputs = this.moduleSpecService.getUiProperties(InputOutputType.Input);
                    this.outputs = this.moduleSpecService.getUiProperties(InputOutputType.Output);
                }),
                untilDestroyed(this),
            )
            .subscribe();

        this.moduleSpecService.specification$
            .pipe(
                tap(() => {
                    this.inputs = this.moduleSpecService.getUiProperties(InputOutputType.Input);
                    this.outputs = this.moduleSpecService.getUiProperties(InputOutputType.Output);
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    isPropertyActive(prop: UIProperty): boolean {
        return this.moduleSpecService.isPropertyActive(prop);
    }

    addInput(): void {
        if (!this.isReadonly) {
            this.onAddInputOutput.emit(InputOutputType.Input);
        }
    }

    addOutput(): void {
        if (!this.isReadonly) {
            this.onAddInputOutput.emit(InputOutputType.Output);
        }
    }

    /**
     * Input should only be removable when:
     * - It's not in Readonly-mode
     * - The form is valid (meaning the right side doesn't show any errors)
     * - Exception: If it's the active property, the form can be inactive (because you should be able to remove an invalid, but active form)
     */
    removeInput(property: UIProperty): void {
        if (!this.isReadonly) {
            this.onRemoveInputOutput.emit({ parameter: property, type: InputOutputType.Input });
        }
    }

    /**
     * Output should only be removable when:
     * - It's not in Readonly-mode
     * - The form is valid (meaning the right side doesn't show any errors)
     * - Exception: If it's the active property, the form can be inactive (because you should be able to remove an invalid, but active form)
     */
    removeOutput(property: UIProperty): void {
        if (!this.isReadonly) {
            this.onRemoveInputOutput.emit({ parameter: property, type: InputOutputType.Output });
        }
    }

    setActiveProperty(property: UIProperty | undefined): void {
        if (!this.isReadonly) {
            this.onSetActiveProp.emit(property);
        }
    }

    getTooltip(property: UIProperty): string {
        return property?.description || '';
    }
}
